import { FC, useEffect, useState } from 'react';
import { captureException } from '@sentry/react';
import { Checkbox, Input } from 'antd';
import { AxiosResponse } from 'axios';
import { TbCalendarShare, TbInfoCircle, TbSearch } from 'react-icons/tb';

import { authorizedAxiosInstance } from '@/clients';
import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { useEntitlements } from '@/hooks/useEntitlements';
import { PlanTag } from '@/shared';
import { PlatformAlert } from '@/shared/components/alerts';
import { BaseSkeleton } from '@/shared/components/skeletons';

interface IndustryProps {
    filters: any;
    handleChange: any;
}

type FMPIndustry = {
    industry: string;
};

const Industry: FC<IndustryProps> = ({ filters, handleChange }): JSX.Element => {
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(false);
    }, []);

    const { hasMinimumPlan } = useEntitlements();

    const [industries, setIndustries] = useState<FMPIndustry[]>([]);

    const handleGetIndustries = async () => {
        try {
            setLoading(true);

            await authorizedAxiosInstance.get(`/api/vendors/fmp/industries`).then((res: AxiosResponse) => {
                if (res?.status === 200) {
                    setIndustries(res?.data);

                    setLoading(false);
                }
            });
        } catch (error) {
            captureException(error);

            setLoading(false);
        }
    };

    useEffect(() => {
        if (hasMinimumPlan('plus')) {
            handleGetIndustries();
        }
    }, []);

    const [searchText, setSearchText] = useState('');

    // Filter by key or value
    const filteredIndustries =
        industries?.length !== 0
            ? industries?.filter((key) => key?.industry?.toLowerCase().includes(searchText?.toLowerCase()))
            : [];

    const { token } = useAntDesignTheme();

    return !hasMinimumPlan('plus') ? (
        <div style={{ textAlign: 'center', margin: '2rem' }}>
            <PlanTag plan={'plus'} style={{ marginLeft: '0.5rem' }} />
        </div>
    ) : (
        <>
            <PlatformAlert
                title='Expanded industry coverage'
                showIcon
                status='info'
                icon={
                    <TbInfoCircle
                        size='1.125rem'
                        style={{
                            marginRight: '0.75rem',
                            marginTop: '0.1rem',
                        }}
                    />
                }
                description={
                    <span>
                        For getting additional information around industry groups and subindustries, please{' '}
                        <a
                            href='http://cal.com/countercyclical/enterprise-demo'
                            target='_blank'
                            rel='noopener noreferrer'
                        >
                            book a demo
                            <TbCalendarShare style={{ verticalAlign: '-0.2rem', marginLeft: '0.125rem' }} />
                        </a>{' '}
                        about an Enterprise plan.
                    </span>
                }
                style={{ marginBottom: '0.75rem' }}
            />
            <Input
                disabled={loading}
                placeholder='Search...'
                suffix={<TbSearch style={{ color: token.colorTextQuaternary }} />}
                value={searchText}
                onChange={(e: any) => setSearchText(e.target.value)}
            />
            {filteredIndustries?.length !== 0 && !loading ? (
                <>
                    <div style={{ marginBottom: '0.5rem' }}>
                        <small style={{ color: token.colorTextTertiary }}>{filteredIndustries?.length} Results</small>
                    </div>
                    <Checkbox.Group
                        value={filters?.industries}
                        onChange={(checkedValues) => {
                            handleChange(['industries'], checkedValues);
                        }}
                    >
                        {filteredIndustries?.map((e) => {
                            return (
                                <div style={{ marginBottom: '0.125rem', width: '100%' }}>
                                    <Checkbox value={e?.industry} />
                                    <span style={{ marginLeft: '0.5rem' }}>{e?.industry}</span>
                                </div>
                            );
                        })}
                    </Checkbox.Group>
                </>
            ) : (
                loading && (
                    <>
                        <BaseSkeleton title active round paragraph={{ rows: 14 }} style={{ marginTop: '1rem' }} />
                    </>
                )
            )}
        </>
    );
};

export default Industry;
